import { useCallback } from "react";
import { useTermSheetContext } from "hooks";
import { hasValueChanged } from "constants/index";

/**
 * useTermSheetCellOperations
 * Custom hook that provides cell value operations for termsheet
 * Handles getting cell values (with changes) and checking if cell has changes
 */
export default function useTermSheetCellOperations() {
  const { changes, conversionMatrix, buyInCurrency, sellInCurrency, termSheet } = useTermSheetContext();

  /**
   * Get the current value of a cell, considering pending changes
   */
  const getCellValue = useCallback(
    (assignment, { key, derivedFrom }) => {
      // Handle derived columns
      if (derivedFrom) {
        return derivedFrom.formula?.(assignment, changes[assignment.id], {
          conversionMatrix,
          sellInCurrency,
          buyInCurrency,
        });
      }

      // Check if there's a change for this cell
      if (changes[assignment.id]?.[key] !== undefined) {
        return changes[assignment.id][key];
      }

      // Return the original value
      return assignment[key];
    },
    [changes, conversionMatrix, buyInCurrency, sellInCurrency]
  );

  /**
   * Check if a specific cell has pending changes
   * Uses normalized comparison to handle type coercion and formatting differences
   */
  const hasChanges = useCallback(
    (assignmentId, columnKey) => {
      const originalValue = termSheet?.assignments.find((a) => a.id === assignmentId)?.[columnKey];
      const changedValue = changes?.[assignmentId]?.[columnKey];

      // If no change entry exists, no changes
      if (changedValue === undefined) return false;

      return hasValueChanged(originalValue, changedValue);
    },
    [changes, termSheet?.assignments]
  );

  return {
    getCellValue,
    hasChanges,
  };
}
